import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api/api';
import { Allergen, BaseFood, ProcessingMethod } from '../../types';

type BaseFoodForm = {
  id: number | null;
  name: string;
  category: string;
  description: string;
  source: string;
  notes: string;
  isActive: boolean;
  energyKcal: string;
  protein: string;
  fat: string;
  carbohydrates: string;
  fiber: string;
  water: string;
  ash: string;
  allergenIds: number[];
};

type ProcessingForm = {
  id: number | null;
  name: string;
  description: string;
  defaultTemperatureC: string;
  defaultRetentionPercent: string;
  energyRetentionPercent: string;
  macroRetentionPercent: string;
  vitaminRetentionPercent: string;
  mineralRetentionPercent: string;
  isActive: boolean;
};

type ProductLinkSuggestion = {
  productId: number;
  productName: string;
  productCategoryName: string | null;
  baseFoodId: number | null;
  baseFoodName: string | null;
  matchScore: number;
  matchReason: string;
};

const emptyBaseFood: BaseFoodForm = {
  id: null,
  name: '',
  category: '',
  description: '',
  source: '',
  notes: '',
  isActive: true,
  energyKcal: '',
  protein: '',
  fat: '',
  carbohydrates: '',
  fiber: '',
  water: '',
  ash: '',
  allergenIds: [],
};

const emptyProcessing: ProcessingForm = {
  id: null,
  name: '',
  description: '',
  defaultTemperatureC: '',
  defaultRetentionPercent: '100',
  energyRetentionPercent: '',
  macroRetentionPercent: '',
  vitaminRetentionPercent: '',
  mineralRetentionPercent: '',
  isActive: true,
};

const n = (value: string) => {
  const parsed = parseFloat(String(value || '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

const optionalN = (value: string) => {
  if (!String(value || '').trim()) return null;
  const parsed = parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const input: React.CSSProperties = { padding: '8px 10px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', width: '100%', boxSizing: 'border-box', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' };
type ButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted' | 'accent';
const btn = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'muted' || tone === 'accent' ? 'var(--crm-surface-alt)' : `var(--crm-${tone}-bg)`,
  color: tone === 'muted' ? 'var(--crm-text-muted)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'muted' ? 'border' : tone === 'accent' ? 'accent' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  padding: '8px 12px',
  cursor: 'pointer',
  fontWeight: 600,
  ...extra,
});
const th: React.CSSProperties = { padding: 9, textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '1px solid var(--crm-border)' };
const td: React.CSSProperties = { padding: 8, borderBottom: '1px solid var(--crm-table-row-border)', verticalAlign: 'top' };

const NutritionBasePage: React.FC = () => {
  const [baseFoods, setBaseFoods] = useState<BaseFood[]>([]);
  const [methods, setMethods] = useState<ProcessingMethod[]>([]);
  const [allergens, setAllergens] = useState<Allergen[]>([]);
  const [search, setSearch] = useState('');
  const [baseForm, setBaseForm] = useState<BaseFoodForm>(emptyBaseFood);
  const [methodForm, setMethodForm] = useState<ProcessingForm>(emptyProcessing);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const importFileRef = useRef<HTMLInputElement>(null);
  const [linkBaseFood, setLinkBaseFood] = useState<BaseFood | null>(null);
  const [productLinks, setProductLinks] = useState<ProductLinkSuggestion[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksSearch, setLinksSearch] = useState('');
  const [detailsBaseFood, setDetailsBaseFood] = useState<BaseFood | null>(null);

  const load = async () => {
    const [baseRes, methodRes, allergenRes] = await Promise.all([
      api.get<BaseFood[]>('/nutrition/base-foods', { params: { includeInactive: true } }),
      api.get<ProcessingMethod[]>('/processing-methods', { params: { includeInactive: true } }),
      api.get<Allergen[]>('/allergens'),
    ]);
    setBaseFoods(baseRes.data);
    setMethods(methodRes.data);
    setAllergens(allergenRes.data);
  };

  useEffect(() => { load().catch((err) => setError(err?.response?.data?.message || 'Не вдалося завантажити нутрієнтну базу.')); }, []);

  const filteredBaseFoods = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return baseFoods;
    return baseFoods.filter((item) =>
      item.name.toLowerCase().includes(term) ||
      item.category.toLowerCase().includes(term) ||
      item.description.toLowerCase().includes(term)
    );
  }, [baseFoods, search]);

  const filteredProductLinks = useMemo(() => {
    const term = linksSearch.trim().toLowerCase();
    if (!term) return productLinks;
    return productLinks.filter((item) =>
      item.productName.toLowerCase().includes(term) ||
      (item.productCategoryName || '').toLowerCase().includes(term) ||
      (item.baseFoodName || '').toLowerCase().includes(term)
    );
  }, [productLinks, linksSearch]);

  const loadProductLinks = async (item: BaseFood) => {
    setLinkBaseFood(item);
    setLinksLoading(true);
    setLinksSearch('');
    setError('');
    try {
      const res = await api.get<ProductLinkSuggestion[]>(`/nutrition/base-foods/${item.id}/product-links`);
      setProductLinks(res.data);
    } catch (err: any) {
      setProductLinks([]);
      setError(err?.response?.data?.message || 'Не вдалося завантажити поради для прив’язки товарів.');
    } finally {
      setLinksLoading(false);
    }
  };

  const updateProductLink = async (productId: number, baseFoodId: number | null) => {
    setError('');
    try {
      await api.put(`/nutrition/base-foods/products/${productId}/base-food`, { baseFoodId });
      setMessage(baseFoodId ? 'Товар прив’язано до базового продукту.' : 'Прив’язку товару видалено.');
      if (linkBaseFood) await loadProductLinks(linkBaseFood);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не вдалося оновити прив’язку товару.');
    }
  };

  const editBaseFood = (item: BaseFood) => setBaseForm({
    id: item.id,
    name: item.name,
    category: item.category,
    description: item.description,
    source: item.source,
    notes: item.notes,
    isActive: item.isActive,
    energyKcal: String(item.energyKcal ?? ''),
    protein: String(item.protein ?? ''),
    fat: String(item.fat ?? ''),
    carbohydrates: String(item.carbohydrates ?? ''),
    fiber: String(item.fiber ?? ''),
    water: String(item.water ?? ''),
    ash: String(item.ash ?? ''),
    allergenIds: item.allergens.map((allergen) => allergen.id),
  });

  const saveBaseFood = async () => {
    setError('');
    if (!baseForm.name.trim()) { setError('Назва базового продукту обов’язкова.'); return; }
    const existing = baseForm.id ? baseFoods.find((item) => item.id === baseForm.id) : null;
    const payload = {
      name: baseForm.name.trim(),
      category: baseForm.category.trim(),
      description: baseForm.description,
      source: baseForm.source,
      notes: baseForm.notes,
      isActive: baseForm.isActive,
      energyKcal: n(baseForm.energyKcal),
      protein: n(baseForm.protein),
      fat: n(baseForm.fat),
      carbohydrates: n(baseForm.carbohydrates),
      fiber: n(baseForm.fiber),
      water: n(baseForm.water),
      ash: n(baseForm.ash),
      nutrients: existing?.nutrients.map((nutrient) => ({
        nutrientKey: nutrient.nutrientKey,
        name: nutrient.name,
        group: nutrient.group,
        unit: nutrient.unit,
        amountPer100g: nutrient.amountPer100g,
      })) ?? [],
      allergenIds: baseForm.allergenIds,
    };
    if (baseForm.id) await api.put(`/nutrition/base-foods/${baseForm.id}`, payload);
    else await api.post('/nutrition/base-foods', payload);
    setBaseForm(emptyBaseFood);
    setMessage('Базовий продукт збережено.');
    await load();
  };

  const editMethod = (item: ProcessingMethod) => setMethodForm({
    id: item.id,
    name: item.name,
    description: item.description,
    defaultTemperatureC: item.defaultTemperatureC != null ? String(item.defaultTemperatureC) : '',
    defaultRetentionPercent: String(item.defaultRetentionPercent),
    energyRetentionPercent: item.energyRetentionPercent != null ? String(item.energyRetentionPercent) : '',
    macroRetentionPercent: item.macroRetentionPercent != null ? String(item.macroRetentionPercent) : '',
    vitaminRetentionPercent: item.vitaminRetentionPercent != null ? String(item.vitaminRetentionPercent) : '',
    mineralRetentionPercent: item.mineralRetentionPercent != null ? String(item.mineralRetentionPercent) : '',
    isActive: item.isActive,
  });

  const saveMethod = async () => {
    setError('');
    if (!methodForm.name.trim()) { setError('Назва типу обробки обов’язкова.'); return; }
    const payload = {
      name: methodForm.name.trim(),
      description: methodForm.description,
      defaultTemperatureC: optionalN(methodForm.defaultTemperatureC),
      defaultRetentionPercent: n(methodForm.defaultRetentionPercent) || 100,
      energyRetentionPercent: optionalN(methodForm.energyRetentionPercent),
      macroRetentionPercent: optionalN(methodForm.macroRetentionPercent),
      vitaminRetentionPercent: optionalN(methodForm.vitaminRetentionPercent),
      mineralRetentionPercent: optionalN(methodForm.mineralRetentionPercent),
      isActive: methodForm.isActive,
    };
    if (methodForm.id) await api.put(`/processing-methods/${methodForm.id}`, payload);
    else await api.post('/processing-methods', payload);
    setMethodForm(emptyProcessing);
    setMessage('Тип обробки збережено.');
    await load();
  };

  const downloadImportTemplate = async () => {
    setError('');
    const response = await api.get('/nutrition/base-foods/import-template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `CRM4Chef_шаблон_нутрієнтна_база_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const importExcel = async (file: File | null | undefined) => {
    if (!file) return;
    setError('');
    setMessage('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post('/nutrition/base-foods/import-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const warnings = Array.isArray(response.data?.warnings) && response.data.warnings.length > 0
        ? ` Попередження: ${response.data.warnings.slice(0, 5).join('; ')}${response.data.warnings.length > 5 ? '…' : ''}`
        : '';
      setMessage(`Імпортовано: ${response.data?.total ?? 0}. Створено: ${response.data?.created ?? 0}, оновлено: ${response.data?.updated ?? 0}.${warnings}`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не вдалося імпортувати Excel-файл.');
    } finally {
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  return (
    <div className="page-shell page-shell--wide nutrition-base-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', borderBottom: '2px solid var(--crm-accent)', paddingBottom: 8 }}>
        <h2 style={{ color: 'var(--crm-text)', margin: 0 }}>Нутрієнтна база продуктів</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={downloadImportTemplate} style={btn('info')}>Завантажити шаблон</button>
          <button type="button" onClick={() => importFileRef.current?.click()} style={btn('accent')}>Імпорт Excel</button>
          <input ref={importFileRef} type="file" accept=".xlsx,.xlsm" style={{ display: 'none' }} onChange={(e) => importExcel(e.target.files?.[0])} />
        </div>
      </div>
      <p style={{ color: 'var(--crm-text-muted)' }}>Це окремий довідник описів продуктів: він не є складом і не бере участі в накладних. Товари CRM прив’язуються до цих базових продуктів для розрахунку харчової цінності.</p>
      {error && <div role="alert" style={{ padding: 10, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 6, marginBottom: 12 }}>{error}</div>}
      {message && <div role="status" style={{ padding: 10, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 6, marginBottom: 12 }}>{message}</div>}

      <section className="nutrition-base-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: 16, alignItems: 'start' }}>
        <div style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', padding: 16, borderRadius: 8, boxShadow: 'var(--crm-shadow-sm)' }}>
          <h3 style={{ marginTop: 0 }}>{baseForm.id ? 'Редагування базового продукту' : 'Новий базовий продукт'}</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <input style={input} placeholder="Назва" value={baseForm.name} onChange={(e) => setBaseForm({ ...baseForm, name: e.target.value })} />
            <input style={input} placeholder="Категорія" value={baseForm.category} onChange={(e) => setBaseForm({ ...baseForm, category: e.target.value })} />
            <textarea style={{ ...input, minHeight: 70 }} placeholder="Опис" value={baseForm.description} onChange={(e) => setBaseForm({ ...baseForm, description: e.target.value })} />
            <div className="nutrition-base-values" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              <input style={input} placeholder="ккал/100г" value={baseForm.energyKcal} onChange={(e) => setBaseForm({ ...baseForm, energyKcal: e.target.value })} />
              <input style={input} placeholder="Білки" value={baseForm.protein} onChange={(e) => setBaseForm({ ...baseForm, protein: e.target.value })} />
              <input style={input} placeholder="Жири" value={baseForm.fat} onChange={(e) => setBaseForm({ ...baseForm, fat: e.target.value })} />
              <input style={input} placeholder="Вугл." value={baseForm.carbohydrates} onChange={(e) => setBaseForm({ ...baseForm, carbohydrates: e.target.value })} />
              <input style={input} placeholder="Клітковина" value={baseForm.fiber} onChange={(e) => setBaseForm({ ...baseForm, fiber: e.target.value })} />
              <input style={input} placeholder="Вода" value={baseForm.water} onChange={(e) => setBaseForm({ ...baseForm, water: e.target.value })} />
              <input style={input} placeholder="Зола" value={baseForm.ash} onChange={(e) => setBaseForm({ ...baseForm, ash: e.target.value })} />
            </div>
            <input style={input} placeholder="Джерело даних" value={baseForm.source} onChange={(e) => setBaseForm({ ...baseForm, source: e.target.value })} />
            <textarea style={{ ...input, minHeight: 55 }} placeholder="Нотатки" value={baseForm.notes} onChange={(e) => setBaseForm({ ...baseForm, notes: e.target.value })} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Алергени</div>
              <div className="nutrition-base-allergen-options" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {allergens.map((allergen) => (
                  <label key={allergen.id} style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 13 }}>
                    <input type="checkbox" checked={baseForm.allergenIds.includes(allergen.id)} onChange={() => setBaseForm((form) => ({ ...form, allergenIds: form.allergenIds.includes(allergen.id) ? form.allergenIds.filter((id) => id !== allergen.id) : [...form.allergenIds, allergen.id] }))} />
                    {allergen.name}
                  </label>
                ))}
              </div>
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={baseForm.isActive} onChange={(e) => setBaseForm({ ...baseForm, isActive: e.target.checked })} /> Активний</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={saveBaseFood} style={btn('success')}>Зберегти</button>
              <button type="button" onClick={() => setBaseForm(emptyBaseFood)} style={btn('muted')}>Очистити</button>
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', padding: 16, borderRadius: 8, boxShadow: 'var(--crm-shadow-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>Базові продукти</h3>
            <input style={{ ...input, maxWidth: 320 }} placeholder="Пошук..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div style={{ maxHeight: 540, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr><th style={th}>Назва</th><th style={th}>Категорія</th><th style={th}></th></tr></thead>
              <tbody>
                {filteredBaseFoods.map((item) => (
                  <tr key={item.id} style={{ opacity: item.isActive ? 1 : .55 }}>
                    <td style={td}>
                      <button
                        type="button"
                        onClick={() => setDetailsBaseFood(item)}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--crm-text)', fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}
                      >
                        {item.name}
                      </button>
                      <div style={{ color: 'var(--crm-text-muted)' }}>{item.description || 'Клікніть назву, щоб переглянути харчову цінність'}</div>
                    </td>
                    <td style={td}>{item.category || '—'}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button type="button" onClick={() => editBaseFood(item)} style={btn('info', { padding: '5px 8px' })}>Ред.</button>
                        <button type="button" onClick={() => loadProductLinks(item)} style={btn('accent', { padding: '5px 8px' })}>Прив’язки</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {detailsBaseFood && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="nutrition-base-detail-modal" style={{ width: '100%', maxWidth: 860, maxHeight: '85vh', overflow: 'auto', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 10, boxShadow: 'var(--crm-shadow-sm)', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>{detailsBaseFood.name}</h3>
                <div style={{ color: 'var(--crm-text-muted)', marginTop: 4 }}>{detailsBaseFood.category || 'Без категорії'} · на 100 г продукту</div>
              </div>
              <button type="button" onClick={() => setDetailsBaseFood(null)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {detailsBaseFood.description && <p style={{ color: 'var(--crm-text)', marginTop: 0 }}>{detailsBaseFood.description}</p>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))', gap: 10, marginBottom: 16 }}>
              {[
                ['Ккал', detailsBaseFood.energyKcal.toFixed(2), ''],
                ['Білки', detailsBaseFood.protein.toFixed(3), 'г'],
                ['Жири', detailsBaseFood.fat.toFixed(3), 'г'],
                ['Вуглеводи', detailsBaseFood.carbohydrates.toFixed(3), 'г'],
                ['Клітковина', detailsBaseFood.fiber.toFixed(3), 'г'],
                ['Вода', detailsBaseFood.water.toFixed(3), 'г'],
                ['Зола', detailsBaseFood.ash.toFixed(3), 'г'],
              ].map(([label, value, unit]) => (
                <div key={label} style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 8, padding: 10 }}>
                  <div style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>{label}</div>
                  <div style={{ color: 'var(--crm-text)', fontWeight: 800, fontSize: 16 }}>{value} {unit}</div>
                </div>
              ))}
            </div>

            <div className="nutrition-base-detail-columns" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <h4 style={{ margin: '0 0 8px', color: 'var(--crm-text)' }}>Вітаміни, мікро- та макроелементи</h4>
                {detailsBaseFood.nutrients.length === 0 ? (
                  <div style={{ color: 'var(--crm-text-muted)', background: 'var(--crm-surface-alt)', padding: 12, borderRadius: 8 }}>Додаткові нутрієнти ще не заповнені. Їх можна імпортувати Excel-шаблоном.</div>
                ) : (
                  <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid var(--crm-border)', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={th}>Група</th>
                          <th style={th}>Нутрієнт</th>
                          <th style={{ ...th, textAlign: 'right' }}>Кількість</th>
                          <th style={th}>Од.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailsBaseFood.nutrients.map((nutrient) => (
                          <tr key={nutrient.id}>
                            <td style={td}>{nutrient.group || 'Other'}</td>
                            <td style={{ ...td, fontWeight: 600 }}>{nutrient.name}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{nutrient.amountPer100g.toFixed(3)}</td>
                            <td style={td}>{nutrient.unit || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div>
                <h4 style={{ margin: '0 0 8px', color: 'var(--crm-text)' }}>Алергени та джерело</h4>
                <div style={{ background: 'var(--crm-surface-alt)', padding: 12, borderRadius: 8, display: 'grid', gap: 10 }}>
                  <div><strong>Алергени:</strong> {detailsBaseFood.allergens.map((allergen) => allergen.name).join(', ') || '—'}</div>
                  <div><strong>Джерело:</strong> {detailsBaseFood.source || '—'}</div>
                  <div><strong>Нотатки:</strong> {detailsBaseFood.notes || '—'}</div>
                  <div><strong>Статус:</strong> {detailsBaseFood.isActive ? 'Активний' : 'Неактивний'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <section style={{ marginTop: 18, background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', padding: 16, borderRadius: 8, boxShadow: 'var(--crm-shadow-sm)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <div>
            <h3 style={{ margin: 0 }}>Поради для прив’язки товарів CRM</h3>
            <div style={{ color: 'var(--crm-text-muted)', fontSize: 13, marginTop: 4 }}>
              {linkBaseFood
                ? <>Базовий продукт: <strong>{linkBaseFood.name}</strong>. Поради рахуються за схожістю назви, B52-назви та категорії.</>
                : 'Натисни «Прив’язки» біля базового продукту, щоб побачити рекомендовані товари CRM.'}
            </div>
          </div>
          <input style={{ ...input, maxWidth: 300 }} placeholder="Пошук товару..." value={linksSearch} onChange={(e) => setLinksSearch(e.target.value)} disabled={!linkBaseFood} />
        </div>

        {!linkBaseFood ? (
          <div style={{ padding: 18, background: 'var(--crm-surface-alt)', borderRadius: 8, color: 'var(--crm-text-muted)' }}>
            Обери базовий продукт у таблиці вище — система покаже товари, які найімовірніше треба до нього прив’язати.
          </div>
        ) : linksLoading ? (
          <div style={{ padding: 18, color: 'var(--crm-text-muted)' }}>Завантаження порад...</div>
        ) : (
          <div style={{ maxHeight: 420, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={th}>Товар CRM</th>
                  <th style={th}>Категорія</th>
                  <th style={th}>Поточна прив’язка</th>
                  <th style={th}>Схожість</th>
                  <th style={th}>Пояснення</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {filteredProductLinks.map((item) => {
                  const linkedHere = item.baseFoodId === linkBaseFood.id;
                  const linkedElsewhere = !!item.baseFoodId && item.baseFoodId !== linkBaseFood.id;
                  return (
                    <tr key={item.productId} style={{ background: linkedHere ? 'var(--crm-success-bg)' : linkedElsewhere ? 'var(--crm-warning-bg)' : undefined }}>
                      <td style={td}><b>{item.productName}</b></td>
                      <td style={td}>{item.productCategoryName || '—'}</td>
                      <td style={td}>{item.baseFoodName || '—'}</td>
                      <td style={{ ...td, fontWeight: 700, color: item.matchScore >= 70 ? 'var(--crm-success)' : item.matchScore >= 35 ? 'var(--crm-warning)' : 'var(--crm-text-muted)' }}>
                        {item.matchScore.toFixed(0)}%
                      </td>
                      <td style={td}>{item.matchReason}</td>
                      <td style={td}>
                        {linkedHere ? (
                          <button type="button" onClick={() => updateProductLink(item.productId, null)} style={btn('danger', { padding: '5px 8px' })}>Відв’язати</button>
                        ) : (
                          <button type="button" onClick={() => updateProductLink(item.productId, linkBaseFood.id)} style={btn(linkedElsewhere ? 'warning' : 'success', { padding: '5px 8px' })}>
                            {linkedElsewhere ? 'Переприв’язати' : 'Прив’язати'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredProductLinks.length === 0 && (
                  <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)', padding: 18 }}>Немає рекомендованих товарів за поточним фільтром.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={{ marginTop: 18, background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', padding: 16, borderRadius: 8, boxShadow: 'var(--crm-shadow-sm)' }}>
        <h3 style={{ marginTop: 0 }}>Типи обробки та коефіцієнти збереження</h3>
        <div className="nutrition-retention-method-form" style={{ display: 'grid', gridTemplateColumns: '2fr 2fr repeat(6, 1fr) auto', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input aria-label="Назва способу обробки" style={input} placeholder="Назва" value={methodForm.name} onChange={(e) => setMethodForm({ ...methodForm, name: e.target.value })} />
          <input aria-label="Опис способу обробки" style={input} placeholder="Опис" value={methodForm.description} onChange={(e) => setMethodForm({ ...methodForm, description: e.target.value })} />
          <input aria-label="Температура за замовчуванням, °C" style={input} placeholder="°C" value={methodForm.defaultTemperatureC} onChange={(e) => setMethodForm({ ...methodForm, defaultTemperatureC: e.target.value })} />
          <input aria-label="Загальне збереження, відсотків" style={input} placeholder="Заг. %" value={methodForm.defaultRetentionPercent} onChange={(e) => setMethodForm({ ...methodForm, defaultRetentionPercent: e.target.value })} />
          <input aria-label="Збереження енергії, відсотків" style={input} placeholder="Енер. %" value={methodForm.energyRetentionPercent} onChange={(e) => setMethodForm({ ...methodForm, energyRetentionPercent: e.target.value })} />
          <input aria-label="Збереження макронутрієнтів, відсотків" style={input} placeholder="Макро %" value={methodForm.macroRetentionPercent} onChange={(e) => setMethodForm({ ...methodForm, macroRetentionPercent: e.target.value })} />
          <input aria-label="Збереження вітамінів, відсотків" style={input} placeholder="Віт. %" value={methodForm.vitaminRetentionPercent} onChange={(e) => setMethodForm({ ...methodForm, vitaminRetentionPercent: e.target.value })} />
          <input aria-label="Збереження мінералів, відсотків" style={input} placeholder="Мін. %" value={methodForm.mineralRetentionPercent} onChange={(e) => setMethodForm({ ...methodForm, mineralRetentionPercent: e.target.value })} />
          <button type="button" onClick={saveMethod} style={btn('success')}>{methodForm.id ? 'Оновити' : 'Додати'}</button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr><th style={th}>Назва</th><th style={th}>Темп.</th><th style={th}>Заг.</th><th style={th}>Енергія</th><th style={th}>Макро</th><th style={th}>Вітаміни</th><th style={th}>Мінерали</th><th style={th}></th></tr></thead>
          <tbody>{methods.map((method) => (
            <tr key={method.id} style={{ opacity: method.isActive ? 1 : .55 }}>
              <td style={td}><b>{method.name}</b><div style={{ color: 'var(--crm-text-muted)' }}>{method.description}</div></td>
              <td style={td}>{method.defaultTemperatureC ?? '—'} °C</td>
              <td style={td}>{method.defaultRetentionPercent}%</td>
              <td style={td}>{method.energyRetentionPercent ?? '—'}%</td>
              <td style={td}>{method.macroRetentionPercent ?? '—'}%</td>
              <td style={td}>{method.vitaminRetentionPercent ?? '—'}%</td>
              <td style={td}>{method.mineralRetentionPercent ?? '—'}%</td>
              <td style={td}><button type="button" onClick={() => editMethod(method)} style={btn('info', { padding: '5px 8px' })}>Ред.</button></td>
            </tr>
          ))}</tbody>
        </table>
      </section>
    </div>
  );
};

export default NutritionBasePage;
